import { Component, OnInit, OnDestroy, inject, Inject, Input, Output, EventEmitter, signal, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DocumentService } from '../services/document.service';
import { EmailService } from '../services/email.service';
import { PhoneService } from '../services/phone.service';
import { getAuthToken } from '../auth.interceptor';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { TableCardLayoutComponent } from '../shared/table-card-layout/table-card-layout.component';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs/internal/Subscription';
import { Store } from '@ngrx/store';
import { PolicyState } from '../store/policy/policy.reducer';
import { finalize } from 'rxjs/operators';
import { LoadingService } from '../shared/loading.service';
import { ToastService } from '../shared/toast/toast.service';
import { ModalService } from '../shared/modal.service';
import { MatDialog } from '@angular/material/dialog';
import { PdfViewerComponent } from '../shared/pdf-viewer/pdf-viewer.component';
import { Renderer2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatMenu } from '@angular/material/menu';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { AppConfig, APP_CONFIG } from 'src/app.config';
import { globalMessages } from '../shared/global-messages';
import { globalConstants } from '../config/global-constants';

interface Document {
    fileName: string;
    category: string;
    type: string;
    sendDate: string;
    receivedDate?: string;
    policyNo?: string;
    attachmentDocId?: string;
    attachmentDocType?: string;
    subCategory?: string;
    source?: string;
}

interface PolicyDetailsResponse {
    [policyNumber: string]: {
        policySnapshot: {
            recentDocuments?: Array<{
                fileName: { node: string; value: string };
                category: { node: string; value: string };
                type: { node: string; value: string };
                sendDate: { node: string; value: string };
                receivedDate?: { node: string; value: string };
                attachmentDocId?: { node: string; value: string };
                attachmentDocType?: { node: string; value: string };
                subCategory?: { node: string; value: string };
                source?: { node: string; value: string };
            }>;
        };
    };
}

@Component({
    selector: 'app-recent-documents',
    imports: [CommonModule, MatIcon, MatMenuModule, ...PSIC_COMPONENTS, TableCardLayoutComponent],
    templateUrl: './recent-documents.component.html',
    styleUrl: './recent-documents.component.scss',
})
export class RecentDocumentsComponent implements OnInit, OnDestroy {
    @Input() isThirdParty = false;
    @Input() isLoaded = false;
    @Input() dmsFailed = false;
    @Output() retry = new EventEmitter<void>();
    globalMessages = globalMessages;

    // Using signals for reactive data management
    documents = signal<Document[]>([]);
    allDocuments = signal<Document[]>([]);
    showAll = signal<boolean>(false);

    showMenu: number | null = null;
    selectedRow: number | null = null;
    private el = inject(ElementRef);
    private documentService = inject(DocumentService);
    private emailService = inject(EmailService);
    private phoneService = inject(PhoneService);
    private modalService = inject(ModalService);
    private combinedSubscription: Subscription;
    private currentPolicyNo: string = '';
    private currentTerm: string = '';
    private loader = inject(LoadingService);
    private toastService = inject(ToastService);
    private dialog = inject(MatDialog);
    private renderer = inject(Renderer2);
    private iconRegistry = inject(MatIconRegistry);
    private currentDialogRef: any = null;
    private dialogSubscriptions = new Subscription();
    private docHubURL: string = '';
    private policyNo$ = this.store.select((state) => state.policy.policyNumber);
    private policyTerm$ = this.store.select((state) => state.policy.policyTerm);

    constructor(
        private store: Store<{ policy: PolicyState }>,
        private http: HttpClient,
        private scrollStrategy: ScrollStrategyOptions,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {}

    ngOnInit() {
        this.http.get<any>('assets/config.json').subscribe((config) => {
            this.docHubURL = config.docHubURL;
        });

        this.combinedSubscription = combineLatest([
            this.store.select((state) => state.policy.data).pipe(filter((data) => !!data)),
            this.store.select((state) => state.policy.policyNumber).pipe(filter((policyNumber) => !!policyNumber)),
            this.store.select((state) => state.policy.policyTerm),
        ]).subscribe(([policyData, policyNumber, policyTerm]) => {
            this.currentPolicyNo = policyNumber!;
            this.currentTerm = policyTerm || '';
            // Load documents from store data if needed (fallback behavior)
            this.loadDocuments(policyData, policyNumber!);
        });
    }

    goToDocHub() {
        combineLatest([this.policyNo$, this.policyTerm$])
            .pipe(take(1))
            .subscribe(([policyNo, term]) => {
                const termValue = this.normalizeTerm(term);
                window.open(
                    `${this.docHubURL}?SourceSystemName=DCPHX&policyNo=${policyNo}&term=${termValue}`,
                    '_blank'
                );
            });
    }

    /**
     * Normalizes a policy term to a two-digit, zero-padded string for DocHub.
     * Handles all edge cases:
     *   - "0" / 0        -> "00" (zero term)
     *   - "1" / 1        -> "01" (first term)
     *   - "00" / "01"    -> unchanged (already padded)
     *   - "10" / 10      -> "10" (no truncation for two+ digit terms)
     *   - null/undefined -> "" (no term available)
     *   - non-numeric    -> "" (invalid term)
     */
    private normalizeTerm(term: string | number | null | undefined): string {
        if (term === null || term === undefined) return '';

        const trimmed = String(term).trim();
        if (trimmed === '') return '';

        const numericTerm = Number(trimmed);
        if (!Number.isFinite(numericTerm) || numericTerm < 0) return '';

        return String(numericTerm).padStart(2, '0');
    }

    private loadDocuments(policyData: any, policyKey: string) {
        // Check for recentDocuments first, then fall back to documents
        const documentsData = policyData[policyKey]?.dmsSnapshot || [];

        let mappedDocuments = documentsData.map((d: any) => ({
            fileName: d.fileName?.value ?? '',
            category: d.category?.value ?? '',
            type: d.type?.value ?? '',
            sendDate: d.sendDate?.value ?? '',
            receivedDate: d.receivedDate?.value ?? '',
            policyNo: policyKey,
            attachmentDocId: d.attachmentDocId?.value ?? '',
            attachmentDocType: d.attachmentDocType?.value ?? '',
            subCategory: d.subCategory?.value ?? '',
            source: d.source?.value ?? '',
        }));

        // Filter documents if isThirdParty is true - only show allowed categories
        if (this.isThirdParty) {
            const allowedCategories = [
                globalConstants.documentCategories.DECLARATIONS,
                globalConstants.documentCategories.INVOICES,
                globalConstants.documentCategories.DECLARATION_PAGE,
                globalConstants.documentCategories.ENDORSEMENTS,
                globalConstants.documentCategories.RENEWAL,
                globalConstants.documentCategories.NON_RENEWAL,
                globalConstants.documentCategories.INSPECTION,
                globalConstants.documentCategories.DIC_MORTGAGEE_NOTICE,
                globalConstants.documentCategories.EVIDENCE_OF_INSURANCE
            ];
            mappedDocuments = mappedDocuments.filter((doc: { category: string; subCategory: string; }) => 
                allowedCategories.includes(doc.category) || 
                allowedCategories.includes(doc.subCategory)
            );
        }

        this.allDocuments.set(mappedDocuments);
        this.showAll.set(false);
        this.documents.set(mappedDocuments.slice(0, 6));
    }

    viewDocument(doc: Document) {
        if (!doc.attachmentDocId) {
            this.toastService.add({
                type: 'error',
                title: '',
                message: 'Document ID is missing.',
            });
            return;
        }

        const downloadRequest = {
            attachmentDocId: doc.attachmentDocId,
            downloadall: 'no',
            policyNo: this.currentPolicyNo || doc.policyNo,
            token: getAuthToken(),
            viewdocument: 'yes',
        };

        this.loader.showLoading();
        this.documentService
            .downloadDocument(downloadRequest)
            .pipe(finalize(() => this.loader.hideLoading()))
            .subscribe({
                next: (data: any) => {
                    if (data.statusCode === 200 && data.linkArr) {
                        const pdfUrl = this.documentService.getDocumentUrl(data.linkArr[0].link);
                        this.openPdfDialog(pdfUrl, doc.fileName, doc.attachmentDocId);
                    } else {
                        this.toastService.add({
                            type: 'error',
                            title: '',
                            message: data.responseMessage || 'Error while opening the document.',
                        });
                    }
                },
                error: () => {
                    this.toastService.add({
                        type: 'error',
                        title: '',
                        message: 'Error while opening the document.',
                    });
                },
            });
    }

    private openPdfDialog(pdfUrl: string, fileName: string, attachmentDocId?: string): void {
        if (this.currentDialogRef) {
            this.dialogSubscriptions.unsubscribe();
            this.dialogSubscriptions = new Subscription();
            this.currentDialogRef.close();
        }
        const uniqueClass = `pdf-dialog-${Math.random().toString(36).substring(2, 11)}`;

        const selectedDoc = this.allDocuments().find((d) => d.attachmentDocId === attachmentDocId);

        this.currentDialogRef = this.dialog.open(PdfViewerComponent, {
            data: {
                pdfUrl,
                fileName,
                attachmentDocId,
                policyNo: this.currentPolicyNo || selectedDoc?.policyNo,
                documentCategory: selectedDoc?.category || '',
                attachmentDocType: selectedDoc?.attachmentDocType || selectedDoc?.category || 'Others',
                subCategory: selectedDoc?.subCategory || '',
            },
            width: '34%',
            height: '80%',
            panelClass: ['pdf-viewer-dialog', uniqueClass],
            maxWidth: '100vw',
            hasBackdrop: false,
            disableClose: false,
            scrollStrategy: this.scrollStrategy.noop(),
            position: { top: '5%', right: '5%' },
        });

        this.setupPinButton(uniqueClass, pdfUrl);
        this.setupDialogCleanup();
    }

    private setupPinButton(uniqueClass: string, pdfUrl: string): void {
        const openSub = this.currentDialogRef.afterOpened().subscribe(() => {
            const dialogElement = document.querySelector(`.${uniqueClass}`);
            if (!dialogElement?.querySelector('.external-pin-button')) {
                const pinButton = this.renderer.createElement('button');
                this.renderer.addClass(pinButton, 'external-pin-button');

                this.iconRegistry.getNamedSvgIcon('pin').pipe(take(1)).subscribe((svgElement) => {
                    this.renderer.appendChild(pinButton, svgElement);
                });

                this.renderer.listen(pinButton, 'click', () => {
                    window.open(pdfUrl, '_blank');
                    this.currentDialogRef.close();
                });

                this.renderer.appendChild(dialogElement, pinButton);
            }
        });

        this.dialogSubscriptions.add(openSub);
    }

    private setupDialogCleanup(): void {
        const closeSub = this.currentDialogRef.afterClosed().subscribe(() => {
            this.currentDialogRef = null;
            this.dialogSubscriptions.unsubscribe();
            this.dialogSubscriptions = new Subscription();
        });
        this.dialogSubscriptions.add(closeSub);
    }

    loadMore() {
        this.showAll.set(true);
        this.documents.set(this.allDocuments());
    }

    showLess() {
        this.showAll.set(false);
        this.documents.set(this.allDocuments().slice(0, 6));
        this.el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    ngOnDestroy() {
        this.combinedSubscription?.unsubscribe();
        this.dialogSubscriptions.unsubscribe();
        this.currentDialogRef?.close();
    }

    toggleMenu(index: number) {
        this.showMenu = this.showMenu === index ? null : index;
    }

    selectRow(index: number) {
        this.selectedRow = this.selectedRow === index ? null : index;
    }

    /**
     * Send document via email
     * Opens a modal dialog to collect recipient email and sends the document
     * @param doc - Document to send via email
     */
    sendDocumentByEmail(doc: Document, menu: MatMenu) {
        menu.closed.emit();

        this.store
            .select((state) => state.policy.data)
            .pipe(take(1))
            .subscribe((data) => {
                const insuredInformation = data?.[this.currentPolicyNo]?.policySnapshot?.contactPreferences?.insuredInformation;

                const popup = {
                    type: 'actionDialog',
                    emailVerified: false,
                    messageTitle: 'Send file by email',
                    messageSubtitle: 'Select the channel through which the link will be sent.',
                    imageSrc: 'assets/images/send-document.svg',
                    imageWidth: '120px',
                    imageHeight: '120px',
                    confirm: 'Send',
                    cancel: 'Cancel',
                    fields: [
                        {
                            type: 'text',
                            caption: 'Email',
                            name: 'email',
                            id: 'email',
                            placeholder: 'Enter email address',
                            required: true,
                            keyboardFilter: 'email',
                            value: '',
                        },
                        {
                            type: 'button',
                            caption: 'Verify Email',
                            name: 'verifyEmail',
                            buttonType: 'borderless',
                            imageSrc: '',
                        },
                    ],
                };

                // Use custom CSS class to ensure higher z-index when PDF viewer is open
                const customClass = this.currentDialogRef ? 'send-via-modal-above-pdf' : undefined;
                
                this.modalService
                    .confirm(popup, customClass, this.isThirdParty ? undefined : insuredInformation)
                    .subscribe({
                        next: (popupCallbackDetail) => {
                            if (popupCallbackDetail?.[0] === 'Send') {
                                this.sendEmail(doc, popupCallbackDetail[1]);
                            }
                        },
                        error: () =>
                            this.showToast('error', 'Email Error', 'Unable to open email dialog. Please try again.'),
                    });
            });
    }

    /**
     * Send email with document link
     * @param doc - Document to send
     * @param recipientEmail - Recipient email address or form data object
     */
    private sendEmail(doc: Document, recipientEmail: string | any) {
        const email = typeof recipientEmail === 'object' ? recipientEmail?.email : recipientEmail;
        if (!this.emailService.validateEmailFormat(email)) {
            this.showToast('error', 'Invalid Email', 'Please enter a valid email address.');
            return;
        }

        this.emailService
            .sendDocumentEmail(this.emailService.buildMultipleDocumentEmailRequest([doc], email))
            .subscribe({
                next: () => this.showToast('success', '', `The ${doc.fileName} was sent successfully.`),
                error: () => this.showToast('error', '', `The ${doc.fileName} couldn't be sent.`),
            });
    }

    /**
     * Send document via SMS
     * Opens a modal dialog to collect recipient mobile number and sends the document
     * @param doc - Document to send via SMS
     */
    sendDocumentBySMS(doc: Document, menu: MatMenu) {
        menu.closed.emit();

        this.store
            .select((state) => state.policy.data)
            .pipe(take(1))
            .subscribe((data) => {
                const insuredInformation = data?.[this.currentPolicyNo]?.policySnapshot?.contactPreferences?.insuredInformation;

                const popup = {
                    type: 'actionDialog',
                    emailVerified: false,
                    messageTitle: 'Send file by SMS',
                    messageSubtitle: 'Select the channel through which the link will be sent.',
                    imageSrc: 'assets/images/send-document.svg',
                    imageWidth: '120px',
                    imageHeight: '120px',
                    confirm: 'Send',
                    cancel: 'Cancel',
                    fields: [
                        {
                            type: 'text',
                            caption: 'Mobile Number',
                            name: 'mobileNumber',
                            id: 'mobileNumber',
                            placeholder: 'Enter mobile number',
                            required: true,
                            value: '',
                            keyboardFilter: 'pint',
                            maxLength: 10,
                            validationMessages: [
                                {
                                    type: 'phoneService',
                                    message: 'This phone number is invalid. Please double-check it.',
                                },
                            ],
                        },
                        {
                            type: 'button',
                            caption: 'Verify Phone',
                            name: 'verifyPhone',
                            buttonType: 'borderless',
                            imageSrc: '',
                        },
                    ],
                };

                // Use custom CSS class to ensure higher z-index when PDF viewer is open
                const customClass = this.currentDialogRef ? 'send-via-modal-above-pdf' : undefined;

                this.modalService
                    .confirm(popup, customClass, this.isThirdParty ? undefined : insuredInformation)
                    .subscribe({
                        next: (popupCallbackDetail) => {
                            if (popupCallbackDetail?.[0] === 'Send') {
                                this.sendSMS(doc, popupCallbackDetail[1]);
                            }
                        },
                        error: () =>
                            this.showToast('error', 'SMS Error', 'Unable to open SMS dialog. Please try again.'),
                    });
            });
    }

    /**
     * Send SMS with document link
     * @param doc - Document to send
     * @param mobileNumber - Recipient mobile number or form data object
     */
    private sendSMS(doc: Document, mobileNumber: string | any) {
        const phone = typeof mobileNumber === 'object' ? mobileNumber?.mobileNumber : mobileNumber;
        if (!doc.policyNo || !doc.attachmentDocId || !doc.subCategory) {
            this.showToast('error', 'SMS Failed', 'Missing required document information.');
            return;
        }

        this.phoneService.sendSMS(phone, doc.policyNo, doc.subCategory, doc.attachmentDocId).subscribe({
            next: () => this.showToast('success', '', `The ${doc.fileName} was sent successfully.`),
            error: () => this.showToast('error', '', `The ${doc.fileName} couldn't be sent.`),
        });
    }

    /**
     * Helper method to show toast notifications
     */
    private showToast(type: 'success' | 'error', title: string, message: string) {
        this.toastService.add({ type, title, message });
    }

    reloadDocuments() {
        this.retry.emit();
    }
}
